/**
 * OfferDetail Component
 *
 * Modal that shows all fields for a single offer from the API.
 * Opens when the admin clicks "View Details" on any row.
 *
 * Includes an Admin Actions panel with Delist / Relist and Delete
 * buttons, each gated behind a confirmation dialog with an optional
 * reason field. Actions call the Rails proxy endpoints with CSRF protection.
 *
 * @component
 */

import React, { useState, useCallback } from "react";
import {
  modalStyles,
  colors,
  gradients,
  buttonStyles,
  adminActionStyles,
} from "./styles";
import {
  formatDate,
  formatAmount,
  getOfferStatus,
  getStatusBadgeStyle,
  getOfferTypeBadgeStyle,
} from "./utils";
import { deleteRequest, patchRequest } from "../../utils/httpRequest";

// ─── Detail row helper ───────────────────────────────────────────────────────

const DetailRow = ({ label, children, tooltip }) => (
  <div style={modalStyles.detailRow}>
    <span style={modalStyles.detailLabel} title={tooltip}>
      {label}
      {tooltip ? " ℹ️" : ""}
    </span>
    <span style={modalStyles.detailValue}>{children ?? "-"}</span>
  </div>
);

const SectionTitle = ({ children }) => (
  <h4 style={modalStyles.sectionTitle}>{children}</h4>
);

// ─── Confirmation Dialog ──────────────────────────────────────────────────────

const ConfirmDialog = ({
  title,
  message,
  confirmLabel,
  confirmStyle,
  onConfirm,
  onCancel,
  loading,
}) => {
  const [reason, setReason] = useState("");

  const handleOverlay = (e) => {
    if (e.target === e.currentTarget && !loading) onCancel();
  };

  return (
    <div style={adminActionStyles.confirmOverlay} onClick={handleOverlay}>
      <div style={adminActionStyles.confirmBox}>
        <h4 style={adminActionStyles.confirmTitle}>{title}</h4>
        <p style={adminActionStyles.confirmText}>{message}</p>

        <label style={adminActionStyles.reasonLabel}>Reason (optional)</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Policy violation, admin cleanup…"
          style={adminActionStyles.reasonInput}
          disabled={loading}
        />

        <div style={adminActionStyles.confirmActions}>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            style={buttonStyles.adminCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason)}
            disabled={loading}
            style={{
              ...confirmStyle,
              ...(loading ? buttonStyles.adminDisabled : {}),
            }}
          >
            {loading ? "Processing…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Admin Actions Panel ──────────────────────────────────────────────────────

const AdminActionsPanel = ({ offer, onActionComplete }) => {
  const [confirmAction, setConfirmAction] = useState(null); // 'delete' | null
  const [actionLoading, setActionLoading] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', message }

  const executeAction = useCallback(
    async (action, reason) => {
      setActionLoading(true);
      setFeedback(null);

      try {
        if (action === "delete") {
          await deleteRequest(
            `/admin/agentic_payment_prediction_offers/delete_offer?offer_id=${encodeURIComponent(
              offer.id,
            )}`,
            { data: { reason: reason || undefined } },
          );
        } else if (action === "delist") {
          await patchRequest(
            `/admin/agentic_payment_prediction_offers/delist_offer?offer_id=${encodeURIComponent(
              offer.id,
            )}`,
            { reason: reason || undefined },
          );
        }

        const labels = { delete: "Deleted", delist: "Delisted" };
        setFeedback({
          type: "success",
          message: `Offer successfully ${labels[action]}.`,
        });
        setConfirmAction(null);

        setTimeout(() => {
          if (onActionComplete) onActionComplete();
        }, 1200);
      } catch (err) {
        console.error(`Admin ${action} error:`, err);
        const message =
          err?.data?.message || err?.message || "Something went wrong.";
        setFeedback({ type: "error", message });
        setConfirmAction(null);
      } finally {
        setActionLoading(false);
      }
    },
    [offer.id, onActionComplete],
  );

  const isDeleted = !!offer.deletedAt;
  const canDelete = !isDeleted;

  return (
    <div style={adminActionStyles.container}>
      <h4 style={adminActionStyles.title}>Admin Actions</h4>

      {/* Feedback alert */}
      {feedback && (
        <div
          style={
            feedback.type === "success"
              ? adminActionStyles.alertSuccess
              : adminActionStyles.alertError
          }
        >
          {feedback.message}
        </div>
      )}

      <div style={adminActionStyles.buttonRow}>
        {/* Delete */}
        {canDelete && (
          <button
            type="button"
            onClick={() => setConfirmAction("delete")}
            disabled={actionLoading}
            style={{
              ...buttonStyles.adminDelete,
              ...(actionLoading ? buttonStyles.adminDisabled : {}),
            }}
            title="Permanently soft-delete this offer, escrow is released and the owner is notified via email"
          >
            Delete Offer
          </button>
        )}

        {isDeleted && (
          <span
            style={{
              color: colors.gray500,
              fontSize: "13px",
              fontStyle: "italic",
            }}
          >
            This offer has been deleted.
          </span>
        )}
      </div>

      {/* Confirmation Dialog */}
      {confirmAction === "delete" && (
        <ConfirmDialog
          title="Delete Offer"
          message={`Are you sure you want to delete this offer (offer_id: ${offer.id})?`}
          confirmLabel="Delete"
          confirmStyle={buttonStyles.adminDelete}
          onConfirm={(reason) => executeAction("delete", reason)}
          onCancel={() => setConfirmAction(null)}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const OfferDetail = ({ offer, onClose, onActionComplete }) => {
  if (!offer) return null;

  const status = getOfferStatus(offer);
  const statusStyle = getStatusBadgeStyle(status);
  const offerTypeBadge = getOfferTypeBadgeStyle(offer.offerType);

  // Close on backdrop click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div style={modalStyles.overlay} onClick={handleOverlayClick}>
      <div style={modalStyles.content}>
        {/* Header */}
        <div style={modalStyles.header}>
          <h3 style={modalStyles.headerTitle}>Offer Details</h3>
          <button
            type="button"
            onClick={onClose}
            style={buttonStyles.ghost}
            onMouseOver={(e) =>
              Object.assign(e.currentTarget.style, buttonStyles.ghostHover)
            }
            onMouseOut={(e) =>
              Object.assign(e.currentTarget.style, buttonStyles.ghostDefault)
            }
          >
            ✕ Close
          </button>
        </div>

        {/* Body */}
        <div style={modalStyles.body}>
          {/* ── Identity Section ── */}
          <SectionTitle>Offer Identity</SectionTitle>
          <DetailRow label="Offer ID">
            <code style={{ fontSize: "12px", wordBreak: "break-all" }}>
              {offer.id}
            </code>
          </DetailRow>
          <DetailRow label="Status">
            <span style={statusStyle}>{status}</span>
          </DetailRow>
          <DetailRow label="Offer Type">
            <span style={offerTypeBadge}>{offer.offerType}</span>
          </DetailRow>

          {/* ── Creator Section ── */}
          <SectionTitle>Creator</SectionTitle>
          <DetailRow label="Creator Email">{offer.userEmail}</DetailRow>
          <DetailRow label="Advertiser">{offer.advertiser}</DetailRow>
          <DetailRow label="User ID">
            <code style={{ fontSize: "12px" }}>{offer.userId}</code>
          </DetailRow>

          {/* ── Offer Config ── */}
          <SectionTitle>Offer Configuration</SectionTitle>
          <DetailRow label="Currency">{offer.currency}</DetailRow>
          <DetailRow label="Base Amount">
            {formatAmount(offer.amount)} {offer.currency}
          </DetailRow>
          <DetailRow label="Leveraged Amount">
            {formatAmount(offer.totalAmount)} USDb
          </DetailRow>
          <DetailRow label="Leverage">{offer.leverage}</DetailRow>
          <DetailRow label="Prediction">{offer.prediction}</DetailRow>
          <DetailRow label="Duration">
            {offer.durationHours != null ? `${offer.durationHours}h` : "-"}
          </DetailRow>
          <DetailRow label="Rate Mode">{offer.rateMode}</DetailRow>
          <DetailRow label="Exchange Rate">{offer.exchangeRate}</DetailRow>
          <DetailRow label="Active">
            <span
              style={{
                padding: "3px 10px",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: 600,
                background: offer.isActive ? colors.success : colors.gray300,
                color: offer.isActive ? colors.white : colors.gray700,
              }}
            >
              {offer.isActive ? "Yes" : "No"}
            </span>
          </DetailRow>

          {/* ── Engagement Cutoff ── */}
          <SectionTitle>Engagement Cutoff</SectionTitle>
          <DetailRow label="Cutoff Type">{offer.cutoffType}</DetailRow>
          <DetailRow label="Cutoff Minutes">
            {offer.cutoffMinutes != null ? `${offer.cutoffMinutes} min` : "-"}
          </DetailRow>
          <DetailRow label="Cutoff Date">
            {formatDate(offer.cutoffDate)}
          </DetailRow>
          <DetailRow label="Engagement Deadline">
            {formatDate(offer.engagementDeadline)}
          </DetailRow>
          <DetailRow label="Can Engage">
            <span
              style={{
                padding: "3px 10px",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: 600,
                background: offer.canEngage ? colors.success : colors.danger,
                color: colors.white,
              }}
            >
              {offer.canEngage ? "Yes" : "No"}
            </span>
          </DetailRow>
          <DetailRow label="Cutoff Passed">
            {offer.isCutoffPassed ? "Yes" : "No"}
          </DetailRow>

          {/* ── Timestamps ── */}
          <SectionTitle>Timestamps</SectionTitle>
          <DetailRow label="Created At">
            {formatDate(offer.createdAt)}
          </DetailRow>
          <DetailRow label="Updated At">
            {formatDate(offer.updatedAt)}
          </DetailRow>
          <DetailRow label="Expires At">
            {formatDate(offer.expiresAt)}
          </DetailRow>
          <DetailRow label="Settled At">
            {formatDate(offer.settledAt)}
          </DetailRow>
          <DetailRow
            label="Delisted At"
            tooltip="Delisted means the offer was removed from the public marketplace. It is no longer visible to traders but is preserved in the admin list. Escrowed funds are released back to the owner."
          >
            {formatDate(offer.delistedAt)}
          </DetailRow>
          {offer.delistedAt && (
            <>
              <DetailRow label="Delisted By">
                <span
                  style={{
                    padding: "3px 10px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: 600,
                    background: "#e67e22",
                    color: colors.white,
                    textTransform: "capitalize",
                  }}
                >
                  {offer.delistedBy || "-"}
                </span>
              </DetailRow>
              <DetailRow label="Delist Reason">
                <span
                  style={{
                    fontSize: "13px",
                    color: colors.gray700,
                    fontStyle: "italic",
                  }}
                >
                  {offer.delistReason || "No reason provided"}
                </span>
              </DetailRow>
            </>
          )}
          <DetailRow
            label="Deleted At"
            tooltip="Deleted means the offer was permanently soft-deleted by an admin. It is hidden from the marketplace and only visible in the admin list with the deleted filter. Escrowed funds are released and the owner is notified."
          >
            {formatDate(offer.deletedAt)}
          </DetailRow>
          {offer.deletedAt && (
            <DetailRow label="Delete Reason">
              <span
                style={{
                  fontSize: "13px",
                  color: colors.gray700,
                  fontStyle: "italic",
                }}
              >
                {offer.deleteReason || "No reason provided"}
              </span>
            </DetailRow>
          )}
          <DetailRow label="Timeline Started">
            {formatDate(offer.timelineStartedAt)}
          </DetailRow>

          {/* ── Trader / Trade Stats ── */}
          <SectionTitle>Trade Statistics</SectionTitle>
          <DetailRow label="Trader Count">{offer.traderCount ?? 0}</DetailRow>
          <DetailRow label="Long Traders">
            {offer.longTraderCount ?? 0}
          </DetailRow>
          <DetailRow label="Short Traders">
            {offer.shortTraderCount ?? 0}
          </DetailRow>
          <DetailRow label="Total Staked">
            {formatAmount(offer.totalStaked)} USDb
          </DetailRow>
          <DetailRow label="Has Engagement">
            {offer.hasEngagement ? "Yes" : "No"}
          </DetailRow>
          <DetailRow label="Time Remaining">
            {offer.timeRemainingMs > 0
              ? `${Math.round(offer.timeRemainingMs / 1000 / 60)} min`
              : "0 (expired)"}
          </DetailRow>

          {/* ── Settlement Outcome ── */}
          {offer.isSettled && (
            <>
              <SectionTitle>Settlement Outcome</SectionTitle>
              <DetailRow label="Outcome">
                <span
                  style={{
                    padding: "3px 10px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: 600,
                    background:
                      offer.settlementOutcome === "long_wins" ||
                      offer.settlementOutcome === "short_wins"
                        ? gradients.success
                        : offer.settlementOutcome === "draw"
                        ? colors.info
                        : colors.gray300,
                    color:
                      offer.settlementOutcome === "draw"
                        ? colors.white
                        : offer.settlementOutcome === "long_wins" ||
                          offer.settlementOutcome === "short_wins"
                        ? colors.white
                        : colors.gray700,
                  }}
                >
                  {(offer.settlementOutcome || "-")
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                </span>
              </DetailRow>
              <DetailRow label="Reason">
                {offer.settlementReason || "-"}
              </DetailRow>

              {/* Winners */}
              {offer.winners && offer.winners.length > 0 && (
                <>
                  <SectionTitle>
                    🏆 Winners ({offer.winners.length})
                  </SectionTitle>
                  <div
                    style={{
                      background: "#f0fdf4",
                      borderRadius: "4px",
                      padding: "12px 16px",
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    {offer.winners.map((p, i) => (
                      <div
                        key={i}
                        style={{
                          padding: "8px 0",
                          borderBottom:
                            i < offer.winners.length - 1
                              ? "1px solid #bbf7d0"
                              : "none",
                          fontSize: "13px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: "6px",
                        }}
                      >
                        <div>
                          <strong style={{ color: "#166534" }}>
                            {p.email || p.name || p.userId}
                          </strong>
                          {p.isOfferCreator && (
                            <span
                              style={{
                                marginLeft: "6px",
                                fontSize: "10px",
                                background: colors.primary,
                                color: colors.white,
                                padding: "1px 6px",
                                borderRadius: "8px",
                                fontWeight: 600,
                              }}
                            >
                              Creator
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "center",
                          }}
                        >
                          <span
                            style={{
                              padding: "2px 8px",
                              borderRadius: "10px",
                              fontSize: "11px",
                              fontWeight: 600,
                              background:
                                p.tradeType === "long" ? "#dcfce7" : "#fce7f3",
                              color:
                                p.tradeType === "long" ? "#166534" : "#9d174d",
                            }}
                          >
                            {p.tradeType}
                          </span>
                          <span style={{ fontWeight: 600, color: "#166534" }}>
                            {formatAmount(p.amount)} USDb
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Losers */}
              {offer.losers && offer.losers.length > 0 && (
                <>
                  <SectionTitle>📉 Losers ({offer.losers.length})</SectionTitle>
                  <div
                    style={{
                      background: "#fef2f2",
                      borderRadius: "4px",
                      padding: "12px 16px",
                      border: "1px solid #fecaca",
                    }}
                  >
                    {offer.losers.map((p, i) => (
                      <div
                        key={i}
                        style={{
                          padding: "8px 0",
                          borderBottom:
                            i < offer.losers.length - 1
                              ? "1px solid #fecaca"
                              : "none",
                          fontSize: "13px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: "6px",
                        }}
                      >
                        <div>
                          <strong style={{ color: "#991b1b" }}>
                            {p.email || p.name || p.userId}
                          </strong>
                          {p.isOfferCreator && (
                            <span
                              style={{
                                marginLeft: "6px",
                                fontSize: "10px",
                                background: colors.primary,
                                color: colors.white,
                                padding: "1px 6px",
                                borderRadius: "8px",
                                fontWeight: 600,
                              }}
                            >
                              Creator
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "center",
                          }}
                        >
                          <span
                            style={{
                              padding: "2px 8px",
                              borderRadius: "10px",
                              fontSize: "11px",
                              fontWeight: 600,
                              background:
                                p.tradeType === "long" ? "#dcfce7" : "#fce7f3",
                              color:
                                p.tradeType === "long" ? "#166534" : "#9d174d",
                            }}
                          >
                            {p.tradeType}
                          </span>
                          <span style={{ fontWeight: 600, color: "#991b1b" }}>
                            {formatAmount(p.amount)} USDb
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* ── Trader Emails ── */}
          {offer.traderEmails && offer.traderEmails.length > 0 && (
            <>
              <SectionTitle>
                Trader Emails ({offer.traderEmails.length})
              </SectionTitle>
              <div
                style={{
                  background: colors.gray100,
                  borderRadius: "4px",
                  padding: "12px 16px",
                  border: `1px solid ${colors.border}`,
                }}
              >
                {offer.traderEmails.map((email, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "4px 0",
                      borderBottom:
                        i < offer.traderEmails.length - 1
                          ? `1px solid ${colors.border}`
                          : "none",
                      fontSize: "13px",
                      color: colors.gray700,
                    }}
                  >
                    {email}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── Admin Actions Panel ── */}
          <AdminActionsPanel
            offer={offer}
            onActionComplete={onActionComplete}
          />
        </div>
      </div>
    </div>
  );
};

export default OfferDetail;
